import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/crm/RichTextEditor";
import { AiComposer } from "@/components/crm/AiComposer";
import {
  UserCircle, Crown, MapPin, ShieldCheck, Envelope, MagnifyingGlass, CheckCircle,
  PaperPlaneTilt, Paperclip, ArrowRight, ArrowLeft, Warning, Lock, UploadSimple, X, Sparkle,
  FileText, ArrowClockwise, Signature,
} from "@phosphor-icons/react";

const CAT_LABEL = { DECISION: "Décision", NEED: "Besoin", PROCUREMENT: "Achat", OTHER: "Autre" };
const STEPS = ["Décideur", "Consultant", "Message"];

export default function OutreachDrawer({ open, onOpenChange, signalId, companyId, companyName, roleTitle, city, onSent }) {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [account, setAccount] = useState(undefined); // undefined=loading, null=none
  const [contacts, setContacts] = useState([]);
  const [canView, setCanView] = useState(false);
  const [canReveal, setCanReveal] = useState(false);
  const [quota, setQuota] = useState(null);
  const [revealingId, setRevealingId] = useState(null);
  const [consultants, setConsultants] = useState([]);
  const [contact, setContact] = useState(null);
  const [consultant, setConsultant] = useState(null);
  const [cSearch, setCSearch] = useState("");
  const [warnings, setWarnings] = useState([]);
  const [sending, setSending] = useState(false);
  const [uploadingCv, setUploadingCv] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [library, setLibrary] = useState([]);

  // composer state
  const [to, setTo] = useState("");
  const [cc, setCc] = useState("");
  const [bcc, setBcc] = useState("");
  const [showCcBcc, setShowCcBcc] = useState(false);
  const [subject, setSubject] = useState("");
  const [bodyHtml, setBodyHtml] = useState("");
  const [attachCv, setAttachCv] = useState(true);
  // relance automatique
  const [followupOn, setFollowupOn] = useState(false);
  const [followupDelay, setFollowupDelay] = useState(4);
  const [followupBody, setFollowupBody] = useState("");
  const [stopOnReply, setStopOnReply] = useState(true);

  const reset = useCallback(() => {
    setStep(0); setContact(null); setConsultant(null); setCSearch("");
    setWarnings([]); setTo(""); setCc(""); setBcc(""); setShowCcBcc(false);
    setSubject(""); setBodyHtml(""); setAttachCv(true);
    setFollowupOn(false); setFollowupDelay(4); setFollowupBody(""); setStopOnReply(true);
  }, []);

  useEffect(() => {
    if (!open) return;
    reset();
    setLoading(true);
    (async () => {
      try {
        const [acc, dm, cs, tpl, lib] = await Promise.all([
          api.get("/crm/email-account").then((r) => r.data).catch(() => null),
          api.get(`/opportunities/${signalId}/decision-makers`).then((r) => r.data).catch(() => ({})),
          api.get("/consultants").then((r) => r.data).catch(() => []),
          api.get("/crm/templates").then((r) => r.data).catch(() => []),
          api.get("/crm/templates/library").then((r) => r.data).catch(() => []),
        ]);
        setAccount(acc);
        setCanView(!!dm.can_view_identity);
        setCanReveal(!!dm.can_reveal_contacts);
        setQuota(dm.contact_quota || null);
        const CATS = ["RECRUITMENT", "DECISION", "NEED", "PROCUREMENT", "OTHER"];
        const local = CATS.flatMap((cat) => (dm[cat] || []).map((c) => ({ ...c, category: cat, scope: "LOCAL" })));
        const hq = (dm.hq && dm.hq.shown)
          ? CATS.flatMap((cat) => (dm.hq[cat] || []).map((c) => ({ ...c, category: cat, scope: "HQ" })))
          : [];
        const seen = new Set();
        const merged = [...local, ...hq].filter((c) => {
          if (!c.id || seen.has(c.id)) return false;
          seen.add(c.id);
          return true;
        });
        setContacts(merged);
        setConsultants(cs);
        setTemplates(tpl);
        setLibrary(Array.isArray(lib) ? lib : []);
      } finally { setLoading(false); }
    })();
  }, [open, signalId, reset]);

  const pickContact = (c) => {
    setContact(c);
    setTo(c.email || "");
    setStep(1);
  };

  const revealContact = async (contactId) => {
    setRevealingId(contactId);
    try {
      const { data: r } = await api.post(`/opportunities/${signalId}/contacts/${contactId}/reveal`);
      const full = r.contact || {};
      setContacts((prev) => prev.map((c) => (String(c.id) === String(contactId)
        ? { ...c, ...full, revealed: true, identity_locked: false } : c)));
      if (r.contact_quota) setQuota(r.contact_quota);
      toast.success(r.charged === false ? "Nom révélé — aucun email disponible, quota non décompté" : "Décideur révélé");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setRevealingId(null);
    }
  };

  const pickConsultant = (c) => {
    setConsultant(c);
    setAttachCv(!!c.cv_file_id);
    // prefill subject + a starter message (no templates yet — just a helpful skeleton)
    const rt = roleTitle || "votre besoin";
    setSubject(`Consultant disponible — ${c.role || c.name}${city ? " · " + city : ""}`);
    setBodyHtml(
      `<p>Bonjour,</p><p>J'ai vu que ${companyName || "votre entreprise"} recrute actuellement sur un poste de <b>${rt}</b>${city ? " à " + city : ""}.</p>` +
      `<p>Nous avons un consultant disponible : <b>${c.name}</b>${c.role ? " — " + c.role : ""}${(c.skills || []).length ? " (" + (c.skills || []).slice(0, 4).join(", ") + ")" : ""}. Vous trouverez son CV en pièce jointe.</p>` +
      `<p>Seriez-vous disponible pour un échange rapide ?</p><p>Bien cordialement,</p>`
    );
    goCompose(c);
  };

  const goCompose = async (c) => {
    setStep(2);
    try {
      const { data } = await api.post("/crm/outreach/preview", {
        contact_id: contact?.id, consultant_id: c?.id, company_id: companyId, signal_id: signalId,
      });
      setWarnings(data.warnings || []);
    } catch { setWarnings([]); }
  };

  const uploadCv = async (file) => {
    if (!file || !consultant) return;
    setUploadingCv(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/crm/consultants/${consultant.id}/cv`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setConsultant((c) => ({ ...c, cv_file_id: data.cv_file_id, cv_filename: data.cv_filename }));
      setConsultants((list) => list.map((x) => x.id === consultant.id ? { ...x, cv_file_id: data.cv_file_id, cv_filename: data.cv_filename } : x));
      setAttachCv(true);
      toast.success("CV enregistré pour ce consultant");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setUploadingCv(false); }
  };

  const send = async () => {
    if (!to.trim()) { toast.error("Le destinataire est requis."); return; }
    if (!subject.trim()) { toast.error("L'objet est requis."); return; }
    if (!bodyHtml.replace(/<[^>]*>/g, "").trim()) { toast.error("Le message est vide."); return; }
    if (followupOn && !followupBody.replace(/<[^>]*>/g, "").trim()) { toast.error("Rédigez le message de relance ou désactivez la relance automatique."); return; }
    setSending(true);
    try {
      const payload = {
        signal_id: signalId, company_id: companyId, contact_id: contact?.id, consultant_id: consultant?.id,
        to_email: to.trim(), to_name: contact?.name, cc: cc.trim(), bcc: bcc.trim(),
        subject: subject.trim(), body_html: bodyHtml, attach_cv: attachCv,
        public_base: process.env.REACT_APP_BACKEND_URL,
      };
      if (followupOn) {
        payload.stop_on_reply = stopOnReply;
        payload.followups = [{ delay_days: Number(followupDelay) || 4, subject: `Re: ${subject.trim()}`, body_html: followupBody }];
      }
      const { data } = await api.post("/crm/outreach/send", payload);
      const fu = data?.followups_scheduled ? ` · relance programmée dans ${followupDelay} j` : "";
      toast.success(`Email envoyé depuis votre adresse professionnelle ✅${fu}`);
      onOpenChange(false);
      onSent?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally { setSending(false); }
  };

  const runAssist = async (payload) => {
    const { data } = await api.post("/crm/outreach/generate", {
      signal_id: signalId, contact_id: contact?.id, consultant_id: consultant?.id, ...payload,
    });
    return data;
  };

  const applyTemplate = async (tid) => {
    if (!tid) return;
    try {
      const { data } = await api.post("/crm/templates/render", {
        template_id: tid, signal_id: signalId, contact_id: contact?.id, consultant_id: consultant?.id,
      });
      if (data.subject) setSubject(data.subject);
      if (data.body_html) setBodyHtml(data.body_html);
      toast.success("Modèle appliqué");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const applyLibrary = async (libId) => {
    const item = library.find((l) => l.id === libId);
    if (!item) return;
    try {
      const { data } = await api.post("/crm/templates/render", {
        subject: item.subject, body_html: item.body_html,
        signal_id: signalId, contact_id: contact?.id, consultant_id: consultant?.id,
      });
      if (data.subject) setSubject(data.subject);
      if (data.body_html) setBodyHtml(data.body_html);
      toast.success("Modèle appliqué — personnalisez-le à votre guise");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const enableFollowup = (on) => {
    setFollowupOn(on);
    if (on && !followupBody.replace(/<[^>]*>/g, "").trim()) {
      const cons = consultant?.name || "notre consultant";
      setFollowupBody(
        `<p>Bonjour,</p><p>Je me permets de revenir vers vous concernant votre mission${roleTitle ? " de " + roleTitle : ""} — le profil de <b>${consultant?.role || cons}</b> y correspond bien.</p>` +
        `<p>Seriez-vous disponible pour un court échange cette semaine ?</p><p>Bien cordialement,</p>`
      );
    }
  };

  const filteredConsultants = consultants.filter((c) => {
    if (!cSearch.trim()) return true;
    const q = cSearch.toLowerCase();
    return [c.name, c.role, c.location, ...(c.skills || [])].join(" ").toLowerCase().includes(q);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-surface border-border max-w-2xl p-0 gap-0 overflow-hidden"
        data-testid="outreach-drawer"
        onInteractOutside={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogTitle className="sr-only">Positionner un consultant</DialogTitle>
        <DialogDescription className="sr-only">Contactez un décideur et positionnez l'un de vos consultants en quelques étapes.</DialogDescription>
        {/* header + stepper */}
        <div className="px-6 pt-5 pb-4 border-b border-border">
          <div className="flex items-center gap-2 mb-3">
            <PaperPlaneTilt size={16} className="text-accent" weight="fill" />
            <h2 className="font-display font-bold text-fg">Positionner un consultant</h2>
          </div>
          <div className="flex items-center gap-2">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-2">
                <div className={`flex items-center gap-1.5 text-xs font-mono px-2 py-1 rounded-full border ${i === step ? "bg-accent/15 border-accent/40 text-accent" : i < step ? "border-accent/20 text-accent/70" : "border-border text-muted"}`} data-testid={`outreach-step-${i}`}>
                  <span className="w-4 h-4 rounded-full flex items-center justify-center text-[10px]" style={{ background: i <= step ? "#00E5FF22" : "transparent" }}>{i < step ? "✓" : i + 1}</span>
                  {s}
                </div>
                {i < STEPS.length - 1 && <ArrowRight size={12} className="text-muted" />}
              </div>
            ))}
          </div>
        </div>

        <div className="px-6 py-5 max-h-[62vh] overflow-y-auto">
          {loading ? (
            <div className="text-sm text-muted animate-pulse py-8 text-center">Chargement…</div>
          ) : (
            <>
              {/* STEP 1 — Contact */}
              {step === 0 && (
                <div data-testid="outreach-contacts">
                  {(!canView && !canReveal) ? (
                    <div className="text-center py-8">
                      <Lock size={30} className="text-accent mx-auto mb-3" />
                      <p className="text-sm text-fg font-medium mb-1">Coordonnées des décideurs verrouillées</p>
                      <p className="text-xs text-muted mb-4">Passez à une offre payante pour voir les noms et emails des décideurs et lancer votre prospection.</p>
                      <Link to="/app/billing" className="text-accent text-sm hover:underline">Voir les offres →</Link>
                    </div>
                  ) : contacts.length === 0 ? (
                    <p className="text-sm text-muted py-6 text-center">Aucun décideur identifié pour ce besoin. Lancez la recherche de décideurs sur la fiche opportunité.</p>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs text-muted">Choisissez le décideur à contacter chez <span className="text-fg">{companyName}</span> :</p>
                        {!canView && quota && !quota.unlimited && (
                          <span className="text-[11px] font-mono text-accent border border-accent/30 bg-accent/10 rounded px-2 py-0.5 shrink-0" data-testid="outreach-quota-chip">
                            {quota.remaining}/{quota.quota} révélations
                          </span>
                        )}
                      </div>
                      {contacts.map((c) => {
                        const remaining = quota && !quota.unlimited ? quota.remaining : null;
                        const outOfQuota = quota && !quota.unlimited && quota.remaining <= 0;
                        const locked = !c.email && !c.revealed && !canView;
                        return (
                          <div key={c.id} data-testid={`outreach-contact-${c.id}`}
                            className="w-full surface-2 border border-border rounded-lg p-3 flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0">
                              {c.category === "DECISION" ? <Crown size={16} className="text-accent" /> : <UserCircle size={16} className="text-accent" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-fg truncate">{c.name || "Décideur vérifié"}</span>
                                <span className="text-[10px] font-mono text-muted border border-border rounded px-1">{CAT_LABEL[c.category]}</span>
                                {c.scope === "HQ" && <span className="text-[10px] font-mono text-[#8A8A93] border border-[#8A8A93]/40 bg-[#8A8A93]/10 rounded px-1" data-testid={`outreach-contact-hq-${c.id}`}>HQ</span>}
                              </div>
                              <div className="text-xs text-muted truncate">{c.title || "—"}</div>
                              <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs mt-1">
                                {(c.city || c.region) && <span className="text-muted flex items-center gap-1"><MapPin size={11} /> {[c.city, c.region].filter(Boolean).join(" · ")}</span>}
                                {c.email ? (
                                  <span className="text-accent flex items-center gap-1"><Envelope size={11} /> {c.email}
                                    {String(c.email_verification_status).toUpperCase() === "VERIFIED" && <ShieldCheck size={11} weight="fill" />}
                                  </span>
                                ) : locked ? (
                                  <span className="text-muted flex items-center gap-1"><Lock size={11} /> Coordonnées masquées</span>
                                ) : <span className="text-muted">Email non disponible</span>}
                              </div>
                            </div>
                            <div className="shrink-0 self-center">
                              {c.email ? (
                                <button onClick={() => pickContact(c)} data-testid={`outreach-pick-${c.id}`}
                                  className="text-xs font-semibold bg-accent text-black px-3 py-1.5 rounded-md hover:brightness-110 flex items-center gap-1 whitespace-nowrap">
                                  Choisir <ArrowRight size={12} weight="bold" />
                                </button>
                              ) : locked && canReveal ? (
                                outOfQuota ? (
                                  <Link to="/app/billing" className="text-xs text-accent hover:underline whitespace-nowrap" data-testid={`outreach-reveal-locked-${c.id}`}>Quota atteint · Offres →</Link>
                                ) : (
                                  <button onClick={() => revealContact(c.id)} disabled={revealingId === c.id} data-testid={`outreach-reveal-${c.id}`}
                                    className="text-xs font-semibold border border-accent/40 bg-accent/10 text-accent px-3 py-1.5 rounded-md hover:bg-accent/20 flex items-center gap-1 whitespace-nowrap disabled:opacity-50">
                                    <Lock size={12} weight="fill" /> {revealingId === c.id ? "Révélation…" : `Révéler${remaining != null ? ` · ${remaining}` : ""}`}
                                  </button>
                                )
                              ) : (
                                <span className="text-[11px] text-muted whitespace-nowrap">Indisponible</span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* STEP 2 — Consultant */}
              {step === 1 && (
                <div data-testid="outreach-consultants">
                  <div className="relative mb-3">
                    <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted" />
                    <Input className="bg-surface-2 border-border pl-9" placeholder="Rechercher un consultant (nom, rôle, compétence…)"
                      value={cSearch} onChange={(e) => setCSearch(e.target.value)} data-testid="outreach-consultant-search" />
                  </div>
                  {consultants.length === 0 ? (
                    <p className="text-sm text-muted py-6 text-center">Aucun consultant. Ajoutez-en depuis <Link to="/app/consultants" className="text-accent hover:underline">Consultants</Link>.</p>
                  ) : (
                    <div className="space-y-2">
                      {filteredConsultants.map((c) => (
                        <button key={c.id} onClick={() => pickConsultant(c)} data-testid={`outreach-consultant-${c.id}`}
                          className="w-full text-left surface-2 border border-border rounded-lg p-3 hover:border-accent/40 transition-colors flex items-start gap-3">
                          <div className="w-9 h-9 rounded-full bg-accent/10 flex items-center justify-center shrink-0"><UserCircle size={16} className="text-accent" /></div>
                          <div className="min-w-0 flex-1">
                            <div className="text-sm font-medium text-fg truncate">{c.name} <span className="text-muted font-normal">· {c.role || c.seniority}</span></div>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(c.skills || []).slice(0, 5).map((s) => <span key={s} className="text-[10px] font-mono bg-surface text-fg/70 border border-border px-1.5 py-0.5 rounded">{s}</span>)}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-muted mt-1">
                              {c.location && <span className="flex items-center gap-1"><MapPin size={11} /> {c.location}</span>}
                              <span className={c.cv_file_id ? "text-accent flex items-center gap-1" : "text-muted flex items-center gap-1"}>
                                <Paperclip size={11} /> {c.cv_file_id ? "CV disponible" : "Pas de CV"}
                              </span>
                            </div>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  <button onClick={() => setStep(0)} className="mt-4 text-xs text-muted hover:text-fg flex items-center gap-1"><ArrowLeft size={12} /> Retour au décideur</button>
                </div>
              )}

              {/* STEP 3 — Compose */}
              {step === 2 && (
                <div data-testid="outreach-composer">
                  {account === null ? (
                    <div className="text-center py-8">
                      <Envelope size={30} className="text-accent mx-auto mb-3" />
                      <p className="text-sm text-fg font-medium mb-1">Connectez votre boîte email professionnelle</p>
                      <p className="text-xs text-muted mb-4">Pour envoyer depuis votre propre adresse (et non depuis interKO), connectez votre boîte email en une minute.</p>
                      <Link to="/app/settings/email" className="inline-flex items-center gap-1.5 bg-accent text-black text-sm font-semibold px-4 py-2 rounded-md hover-lift">Connecter ma boîte email</Link>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {warnings.length > 0 && (
                        <div className="space-y-2" data-testid="outreach-warnings">
                          {warnings.map((w, i) => (
                            <div key={i} className="flex items-start gap-2 bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-md px-3 py-2 text-xs text-[#F5C877]">
                              <Warning size={14} className="shrink-0 mt-0.5" /> <span>{w.message}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <Row label="De">
                        <div className="text-sm text-fg font-mono flex items-center gap-2" data-testid="outreach-from">
                          <CheckCircle size={13} className="text-accent" weight="fill" /> {account?.from_name ? `${account.from_name} <${account.email}>` : account?.email}
                        </div>
                      </Row>
                      <Row label="À">
                        <Input className="bg-surface-2 border-border" value={to} onChange={(e) => setTo(e.target.value)} data-testid="outreach-to" />
                      </Row>
                      {!showCcBcc ? (
                        <button onClick={() => setShowCcBcc(true)} className="text-xs text-accent hover:underline">+ Cc / Cci</button>
                      ) : (
                        <>
                          <Row label="Cc"><Input className="bg-surface-2 border-border" value={cc} onChange={(e) => setCc(e.target.value)} data-testid="outreach-cc" placeholder="séparés par des virgules" /></Row>
                          <Row label="Cci"><Input className="bg-surface-2 border-border" value={bcc} onChange={(e) => setBcc(e.target.value)} data-testid="outreach-bcc" /></Row>
                        </>
                      )}
                      {/* Assistant de rédaction IA (ton, longueur, consignes, variantes) */}
                      <AiComposer
                        run={runAssist}
                        getCurrent={() => ({ subject, body_html: bodyHtml })}
                        onApply={(v) => { if (v.subject) setSubject(v.subject); setBodyHtml(v.body_html || ""); }}
                        defaultVariants={2}
                        testid="outreach-ai"
                      />
                      {/* Modèles : org + bibliothèque riche */}
                      <div className="flex items-center gap-2 flex-wrap" data-testid="outreach-compose-tools">
                        {templates.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <FileText size={13} className="text-muted" />
                            <Select onValueChange={applyTemplate}>
                              <SelectTrigger className="h-8 bg-surface border-border text-xs w-44" data-testid="outreach-template-select"><SelectValue placeholder="Mes modèles" /></SelectTrigger>
                              <SelectContent>{templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        {library.length > 0 && (
                          <div className="flex items-center gap-1.5">
                            <Sparkle size={13} className="text-accent" />
                            <Select onValueChange={applyLibrary}>
                              <SelectTrigger className="h-8 bg-surface border-border text-xs w-52" data-testid="outreach-library-select"><SelectValue placeholder="Bibliothèque de modèles" /></SelectTrigger>
                              <SelectContent>{library.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        )}
                        <Link to="/app/settings/templates" className="text-xs text-muted hover:text-fg ml-auto">Gérer les modèles</Link>
                      </div>
                      <Row label="Objet">
                        <Input className="bg-surface-2 border-border" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="outreach-subject" />
                      </Row>
                      <RichTextEditor value={bodyHtml} onChange={setBodyHtml} testid="outreach-body" />
                      {account?.signature_html ? (
                        <div className="mt-1" data-testid="outreach-signature-block">
                          <div className="text-[10px] text-muted flex items-center gap-2 my-1">
                            <span className="flex-1 border-t border-dashed border-border" /> Signature (ajoutée automatiquement) <span className="flex-1 border-t border-dashed border-border" />
                          </div>
                          <div className="rounded-md border border-border bg-surface-2/40 p-2.5 text-xs text-fg/80" dangerouslySetInnerHTML={{ __html: account.signature_html }} />
                          <Link to="/app/settings/email" className="text-[11px] text-accent hover:underline mt-1 inline-block">Modifier ma signature</Link>
                        </div>
                      ) : (
                        <div className="mt-1 flex items-center justify-between gap-2 rounded-md border border-dashed border-border bg-surface-2/30 px-3 py-2" data-testid="outreach-signature-empty">
                          <span className="text-[11px] text-muted flex items-center gap-1.5"><Signature size={13} className="text-accent" /> Aucune signature configurée — elle sera ajoutée automatiquement à vos emails.</span>
                          <Link to="/app/settings/email" className="text-[11px] text-accent hover:underline shrink-0">Ajouter une signature</Link>
                        </div>
                      )}

                      {/* attachment */}
                      <div className="surface-2 border border-border rounded-md px-3 py-2.5" data-testid="outreach-attachment">
                        <div className="overline mb-2 flex items-center gap-1.5"><Paperclip size={12} /> Pièce jointe</div>
                        {consultant?.cv_file_id ? (
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input type="checkbox" checked={attachCv} onChange={(e) => setAttachCv(e.target.checked)} className="accent-[#00E5FF]" data-testid="outreach-attach-cv" />
                            <Paperclip size={13} className="text-accent" /> {consultant.cv_filename || "CV du consultant"}
                          </label>
                        ) : (
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-muted">Aucun CV pour {consultant?.name}.</span>
                            <label className="text-xs text-accent hover:underline cursor-pointer flex items-center gap-1">
                              <UploadSimple size={13} /> {uploadingCv ? "Envoi…" : "Téléverser un CV"}
                              <input type="file" accept=".pdf,.doc,.docx" className="hidden" data-testid="outreach-cv-upload"
                                onChange={(e) => uploadCv(e.target.files?.[0])} />
                            </label>
                          </div>
                        )}
                      </div>
                      {/* Relance automatique */}
                      <div className="surface-2 border border-border rounded-md px-3 py-2.5" data-testid="outreach-followup">
                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                          <input type="checkbox" checked={followupOn} onChange={(e) => enableFollowup(e.target.checked)} className="accent-[#00E5FF]" data-testid="outreach-followup-toggle" />
                          <ArrowClockwise size={13} className="text-accent" /> Programmer une relance automatique
                        </label>
                        {followupOn && (
                          <div className="mt-3 space-y-2.5 pl-6" data-testid="outreach-followup-config">
                            <div className="flex items-center gap-2 text-xs text-muted">
                              <span>Envoyer la relance après</span>
                              <Select value={String(followupDelay)} onValueChange={(v) => setFollowupDelay(Number(v))}>
                                <SelectTrigger className="h-7 w-24 bg-surface border-border text-xs" data-testid="outreach-followup-delay"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                  {[2, 3, 4, 5, 7, 10, 14].map((d) => <SelectItem key={d} value={String(d)}>{d} jours</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <label className="flex items-center gap-2 text-xs text-fg/80 cursor-pointer">
                              <input type="checkbox" checked={stopOnReply} onChange={(e) => setStopOnReply(e.target.checked)} className="accent-[#00E5FF]" data-testid="outreach-followup-stop" />
                              Ne pas relancer si le décideur répond
                            </label>
                            <div>
                              <div className="overline mb-1">Message de relance</div>
                              <RichTextEditor value={followupBody} onChange={setFollowupBody} testid="outreach-followup-body" />
                            </div>
                          </div>
                        )}
                      </div>
                      <button onClick={() => setStep(1)} className="text-xs text-muted hover:text-fg flex items-center gap-1"><ArrowLeft size={12} /> Changer de consultant</button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* footer */}
        {step === 2 && account && (
          <div className="px-6 py-4 border-t border-border flex items-center justify-between gap-3">
            <div className="text-xs text-muted flex items-center gap-1.5 min-w-0">
              <Sparkle size={12} className="text-accent shrink-0" /> <span className="truncate">{contact?.name} · {consultant?.name}</span>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => onOpenChange(false)} disabled={sending} data-testid="outreach-cancel-btn"
                className="text-sm font-semibold text-muted hover:text-fg px-4 py-2.5 rounded-md border border-border hover:border-fg/30 transition-colors disabled:opacity-60">
                Annuler
              </button>
              <button onClick={send} disabled={sending} data-testid="outreach-send-btn"
                className="flex items-center gap-2 bg-accent text-black font-semibold px-5 py-2.5 rounded-md hover-lift disabled:opacity-60">
                <PaperPlaneTilt size={16} weight="fill" /> {sending ? "Envoi…" : "Envoyer"}
              </button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex items-center gap-3">
      <span className="overline w-12 shrink-0">{label}</span>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  );
}
